import { PRICING_TYPES } from '@constants';
import { fleetVehiclesServices, pricingServices } from '@services';
import { showError } from '@utils';
import { QUERY_KEYS } from 'constants/queryKeys';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';

export const useFleetVehiclePricing = ({ vehicle, skuType }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [pricing, setPricing] = useState([]);

  const pricingResponse = useQuery(
    [QUERY_KEYS.GET_VEHICLE_PRICING_KEY, vehicle.id],
    () => pricingServices.getVehiclePricing(vehicle.id),
    { enabled: !!vehicle.id, refetchOnMount: false }
  );

  const getUpdatedFixedPrices = async (currentFixedPrices, values) => {
    return Promise.all(
      currentFixedPrices.map((item) =>
        pricingServices.getPricing({
          price: values?.price,
          duration: item?.monthsDuration,
          skuType,
        })
      )
    );
  };

  const exposeToCatalog = pricingResponse?.data?.data?.exposeToCatalog || false;

  const handleUpdatePricing = async (values) => {
    let newPricing = [];
    let currentOpenPrice = pricing?.find((item) => {
      return item?.type === PRICING_TYPES?.open;
    });
    let currentFixedPrices = pricing?.filter((item) => {
      return item?.type === PRICING_TYPES?.fixed;
    });
    let updatedFixedData = [];
    if (
      values?.type === PRICING_TYPES.open &&
      values?.price &&
      Number(currentOpenPrice?.price) !== values?.price
    ) {
      try {
        setIsLoading(true);

        updatedFixedData = await getUpdatedFixedPrices(
          currentFixedPrices,
          values
        );
      } catch (error) {
        showError(error);
      } finally {
        setIsLoading(false);
      }
    }

    if (values?.type === PRICING_TYPES.open) {
      currentOpenPrice = {
        ...values,
        price: `${values.price || 0}`,
        signUpFee: `${values.signUpFee || ''}`,
        initialPrice: `${values.initialPrice || 0}`,
      };
    }
    currentFixedPrices = currentFixedPrices?.map((item, index) => {
      if (updatedFixedData?.length && updatedFixedData[index]) {
        return {
          ...item,
          price: updatedFixedData[index]?.data?.price,
        };
      }
      if (item?.id === values?.id) {
        return {
          ...values,
          price: `${values?.price || 0}`,
          signUpFee: `${values?.signUpFee || 0}`,
          initialPrice: `${
            item?.type === values?.type ? values.initialPrice || 0 : 0
          }`,
        };
      }
      return { ...item };
    });
    newPricing = [currentOpenPrice, ...currentFixedPrices];
    setPricing(newPricing);
  };

  const handleSavePricing = async (isExposeToCatalog) => {
    try {
      setIsLoading(true);
      let payload = {
        pricing: [],
        exposeToCatalog: isExposeToCatalog,
      };
      if (isExposeToCatalog && pricing.length) {
        const newPricing = pricing?.map((item) => {
          return {
            ...item,
            signUpFee: item?.signUpFee || null,
          };
        });

        const openPrice = newPricing?.find((item) => {
          return item?.type === PRICING_TYPES.open;
        });

        if (!openPrice?.price || !Number(openPrice?.price)) {
          showError('Please fill open monthly price');
          return;
        }

        payload = {
          pricing: newPricing,
          exposeToCatalog: isExposeToCatalog,
        };
      }
      await fleetVehiclesServices?.updateVehiclePricing(vehicle.id, payload);
      await pricingResponse?.refetch();
      setIsLoading(false);
    } catch (e) {
      showError(e);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (pricingResponse?.data?.data?.pricing) {
      setPricing(pricingResponse.data.data.pricing);
    }
  }, [pricingResponse?.data?.data?.pricing]);

  return {
    pricing,
    exposeToCatalog,
    isLoadingPrices: pricingResponse?.isLoading || isLoading,
    handleUpdatePricing,
    handleSavePricing,
    refetch: pricingResponse?.refetch,
  };
};
